/**
 * AuthEventBus - Central event system for authentication state changes
 * Allows components and services to react to auth updates in real-time
 */

type EventCallback = (data?: any) => void;

export type AuthEventType = 
  | 'token-updated'
  | 'token-refreshed'
  | 'eventId-changed'
  | 'user-updated'
  | 'logout'
  | 'login'
  | 'auth-error';

class AuthEventBus {
  private events: Map<AuthEventType, Set<EventCallback>> = new Map();
  private debugMode = true;

  constructor() {
    // Initialize all event types
    const eventTypes: AuthEventType[] = [
      'token-updated',
      'token-refreshed', 
      'eventId-changed',
      'user-updated',
      'logout',
      'login',
      'auth-error'
    ];
    
    eventTypes.forEach(type => {
      this.events.set(type, new Set());
    });

    if (this.debugMode) {
      console.log('[AuthEventBus] Initialized with event types:', eventTypes);
    }
  }

  /**
   * Subscribe to an authentication event
   */
  on(event: AuthEventType, callback: EventCallback): () => void {
    const callbacks = this.events.get(event);
    if (!callbacks) {
      console.error(`[AuthEventBus] Unknown event type: ${event}`);
      return () => {};
    }

    callbacks.add(callback);
    
    if (this.debugMode) {
      console.log(`[AuthEventBus] Listener added for event: ${event}`, {
        totalListeners: callbacks.size
      });
    }

    // Return unsubscribe function
    return () => {
      callbacks.delete(callback);
      if (this.debugMode) {
        console.log(`[AuthEventBus] Listener removed for event: ${event}`, {
          remainingListeners: callbacks.size
        });
      }
    };
  }

  /**
   * Emit an authentication event
   */
  emit(event: AuthEventType, data?: any): void {
    const callbacks = this.events.get(event);
    if (!callbacks || callbacks.size === 0) {
      if (this.debugMode) {
        console.log(`[AuthEventBus] No listeners for event: ${event}`);
      }
      return;
    }

    if (this.debugMode) {
      console.log(`[AuthEventBus] Emitting event: ${event}`, {
        data,
        listeners: callbacks.size
      });
    }

    // Execute all callbacks
    callbacks.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`[AuthEventBus] Error in listener for ${event}:`, error);
      }
    });
  }

  /**
   * Remove all listeners for a specific event
   */
  removeAllListeners(event?: AuthEventType): void {
    if (event) {
      const callbacks = this.events.get(event);
      if (callbacks) {
        callbacks.clear();
        console.log(`[AuthEventBus] All listeners removed for event: ${event}`);
      }
    } else {
      // Clear all events
      this.events.forEach((callbacks, _eventType) => {
        callbacks.clear();
      });
      console.log('[AuthEventBus] All listeners removed for all events');
    }
  }

  /**
   * Get the number of listeners for an event
   */
  listenerCount(event: AuthEventType): number {
    const callbacks = this.events.get(event);
    return callbacks ? callbacks.size : 0;
  }

  /**
   * Enable or disable debug mode
   */
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
    console.log(`[AuthEventBus] Debug mode ${enabled ? 'enabled' : 'disabled'}`);
  }
}

// Export singleton instance
export const authEventBus = new AuthEventBus();

// Export for window debugging
if (typeof window !== 'undefined') {
  (window as any).authEventBus = authEventBus;
  console.log('🚌 AuthEventBus available at window.authEventBus for debugging');
}